import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import toast from "react-hot-toast";
import "./AdminNewsletterSubscriptionsPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminNewsletterSubscriptionsPage() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchSubscriptions();
  }, [navigate]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");

    try {
      const response = await fetch(`${API_BASE}/newsletter-subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const responseData = await response.json();
        
        if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
          setSubscriptions(responseData.data);
          setTotalCount(responseData.data.length);
        } else {
          setSubscriptions([]);
          setTotalCount(0);
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to fetch newsletter subscriptions");
        setSubscriptions([]);
        setTotalCount(0);
      }
    } catch (error) {
      toast.error("Error fetching newsletter subscriptions");
      console.error("Error:", error);
      setSubscriptions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };


  const copyAllEmails = async () => {
    if (subscriptions.length === 0) {
      toast.error("No emails to copy");
      return;
    }

    const emailsToCopy = subscriptions
      .map(sub => sub.email)
      .filter(email => email);

    if (emailsToCopy.length === 0) {
      toast.error("No emails to copy");
      return;
    }

    const emailsString = emailsToCopy.join(", ");

    try {
      await navigator.clipboard.writeText(emailsString);
      toast.success(`Copied ${emailsToCopy.length} email addresses to clipboard`);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = emailsString;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(`Copied ${emailsToCopy.length} email addresses to clipboard`);
    }
  };

  const copySingleEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied to clipboard");
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Email copied to clipboard");
    }
  };

  const filteredSubscriptions = subscriptions.filter(subscription =>
    subscription.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subscription.id?.toString().includes(searchTerm)
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };


  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-newsletter-subscriptions">
          <div className="loading">Loading newsletter subscriptions...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-newsletter-subscriptions">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Newsletter Subscriptions</h1>
            <p>Manage newsletter email subscriptions</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn-primary" 
              onClick={copyAllEmails} 
              disabled={subscriptions.length === 0}
            >
              <svg viewBox="0 0 24 24">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
              </svg>
              Copy All Emails ({totalCount})
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="search-container">
          <div className="search-input">
            <svg viewBox="0 0 24 24">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" fill="none" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <input
              type="text"
              placeholder="Search by email or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-number">{totalCount}</div>
            <div className="stat-label">Total Subscriptions</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{filteredSubscriptions.length}</div>
            <div className="stat-label">Filtered Results</div>
          </div>
        </div>

        {/* Subscriptions Table */}
        {filteredSubscriptions.length > 0 ? (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Subscribed At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td>
                      <strong>#{subscription.id}</strong>
                    </td>
                    <td>
                      <div className="email-cell">
                        <span className="email-address">{subscription.email}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${subscription.status === 'active' ? 'active' : 'inactive'}`}>
                        {subscription.status || 'active'}
                      </span>
                    </td>
                    <td>
                      {formatDate(subscription.subscribed_at || subscription.created_at)}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-copy"
                          onClick={() => copySingleEmail(subscription.email)}
                          title="Copy Email"
                        >
                          <svg viewBox="0 0 24 24">
                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
                          </svg>
                          Copy
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" fill="none" stroke="currentColor" strokeWidth="2"/>
                <polyline points="22,6 12,13 2,6" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h3>No Newsletter Subscriptions Found</h3>
            <p>
              {searchTerm 
                ? `No subscriptions match your search for "${searchTerm}"`
                : "There are no newsletter subscriptions to display"
              }
            </p>
            {searchTerm && (
              <button className="btn-secondary" onClick={() => setSearchTerm("")}>
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
